import "./VisualAttention.scss";
import React, { useState, useEffect, useRef } from "react";
import cx from "classnames";
import useIdleTimer from "../Reader/Phrase/hooks/useIdleTimer";

import AmiraIconButton2, {
  ICON_BUTTON_TYPES,
} from "../Global/AmiraIconButton2/AmiraIconButton2";
import { PhraseTriggers } from "../../constants/constants";

const IDLE_ENCOURAGEMENT_TIMEOUT = 10000;
const TASK_AUTO_SUBMIT_TIMEOUT = 60000;

function VisualAttention({
  isInstruction,
  targetOption,
  targetImage,
  allOptions,
  gridWidth,
  repeatInstructions,
  speech,
  setHideParentRepeatButton,
  currentIntroductionTrigger,
  setArrayResponse,
  startPhrase
}) {
  const [optionRows, setOptionRows] = useState([]);
  const [selectionArray, setSelectionArray] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [targetOptionVisible, setTargetOptionVisible] = useState(false);
  const [gridVisible, setGridVisible] = useState(false);
  const [idlePlayed, setIdlePlayed] = useState(false);
  const [canSelect, setCanSelect] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(true);
  const encouragementText = `You can do it. ${repeatInstructions ? repeatInstructions : ""}`;
  const [taskTimeoutExpired, setTaskTimeoutExpired] = useState(false);

  // Grid Sizing
  const containerRef = useRef(null);
  const [optionImageSize, setOptionImageSize] = useState(0);
  const [optionWrapperWidth, setOptionWrapperWidth] = useState(0);

  const taskTimeoutRef = useRef(null);

  const { resetIdleTimer, idleSpeechPlaying, clearIdleTimer } = useIdleTimer(
    speech,
    IDLE_ENCOURAGEMENT_TIMEOUT,
    TASK_AUTO_SUBMIT_TIMEOUT
  );

  useEffect(() => {
    if (setHideParentRepeatButton) {
      setHideParentRepeatButton(true);
    }

    return () => {
      if (setHideParentRepeatButton) {
        setHideParentRepeatButton(false);
      }
      if (taskTimeoutRef.current) {
        clearTimeout(taskTimeoutRef.current);
      }
    }
  }, []);

  // Grid Sizing
  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const width = container.clientWidth;
      const height = container.clientHeight;

      const rows = optionRows.length;

      const wrapperWidth = (width / gridWidth);
      const itemSize = (height / rows) - 10;

      setOptionWrapperWidth(wrapperWidth);
      setOptionImageSize(itemSize);
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [optionRows.length, gridWidth]);

  useEffect(() => {
    if (idleSpeechPlaying) {
      setIdlePlayed(true);
      clearIdleTimer()
    }
  }, [idleSpeechPlaying]);

  useEffect(() => {
    if (isInstruction) {
      setCanSelect(false);
    } else {
      setCanSelect(true);
    }

    if (!isInstruction && !idlePlayed) {
      resetIdleTimer(encouragementText);
    }
  }, [isInstruction]);

  useEffect(() => {
    if (taskTimeoutExpired) {
      setCanSelect(false);
      setSubmitEnabled(false);

      speech.speakWithPromise({
        textToSpeak: "Let's move on."
      }).then(() => {
        setArrayResponse(selectionArray, true);
      });
    }
  }, [taskTimeoutExpired]);

  // Format options and initialize selection array when options change
  useEffect(() => {
    const formattedOptions = allOptions.map(option => ({
      key: option.key,
      imageUrl: option.imageUrl.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT)
    }));
    const optionRows = chunkArray(formattedOptions, gridWidth);
    setOptionRows(optionRows);
    if (allOptions.length > 0) {
      const initialSelections = new Array(allOptions.length).fill(0);
      setSelectionArray(initialSelections);
    }
  }, [allOptions]);

  useEffect(() => {
    setArrayResponse(selectionArray);
  }, [selectionArray]);

  useEffect(() => {
    console.log("formattedOptions", optionRows);
  }, [optionRows]);

  useEffect(() => {
    if (currentIntroductionTrigger) {
      handleTrigger(currentIntroductionTrigger);
    }
  }, [currentIntroductionTrigger]);

  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }

  const toggleImageSelection = (index) => {
    if (!canSelect) return;
    clearIdleTimer();

    setSelectionArray((prevArray) => {
      const newArray = [...prevArray];
      newArray[index] = newArray[index] === 0 ? 1 : 0; // Toggle between 0 and 1      
      return newArray;
    });
  };

  const handleTrigger = (trigger) => {
    switch (trigger) {
      case PhraseTriggers.ShowTargetAnimal:
        setTimeout(() => {
          setTargetOptionVisible(true);
        }, 2000);
        break;
      case PhraseTriggers.ShowAllAnimals:
        setGridVisible(true);
        setStartTime(Date.now());
        taskTimeoutRef.current = setTimeout(() => {
          taskTimeoutRef.current = undefined;
          setTaskTimeoutExpired(true);
        }, TASK_AUTO_SUBMIT_TIMEOUT);
        startPhrase()
        break;
      default:
        break;
    }
  };

  const handleButtonClick = () => {
    if (!submitEnabled) return;
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;

    if (elapsedTime >= 10000) {
      if (taskTimeoutRef.current) {
        clearTimeout(taskTimeoutRef.current);
        taskTimeoutRef.current = undefined;
      }
      setArrayResponse(selectionArray, true);
    } else {
      //does nothing
      console.log("Submit clicked before 10 seconds");
    }
  };

  return (
    <div className="visual-attention-container">
      <div className="target-image-container">
        <div className="target-image-border">
          <div className="target-image-inner-border">
            <img src={targetImage} alt={targetOption} />
            {!targetOptionVisible && <div className="white-box-overlay"></div>}
          </div>
        </div>
      </div>

      <div 
        className="image-grid" 
        style={{ gridTemplateColumns: `repeat(${gridWidth}, ${optionImageSize}px)` }} 
        ref={containerRef}
      >
        {gridVisible &&
          optionRows.map((rowOptions, rowIndex) => {
            return (
              <div className="grid-item-row" key={`row-${rowIndex}`}>
                {rowOptions.map((option, columnIndex) => {
                  const index = rowIndex * gridWidth + columnIndex;
                  return (
                    <div 
                      key={`item-${index}`}
                      className="grid-item-wrapper"
                      style={{ 
                      width: `${optionWrapperWidth}px`,
                      height: `${optionImageSize}px`, 
                      }}
                    >
                      <img 
                        src={option.imageUrl} 
                        alt={`Grid item ${index + 1}`}
                        onClick={() => toggleImageSelection(index)}
                        className={cx("item", {
                          selected: selectionArray[index] === 1,
                        })}
                        style={{ 
                          width: `${optionImageSize - 10}px`,
                          height: `${optionImageSize - 10}px`, 
                        }}
                      />
                    </div>
                  );
                })}
              </div>  
            );
          })}
      </div>

      {gridVisible && (
        <div className="bottom-container">
          <div className="divider" />

          <div className="submit-button-container">
            <AmiraIconButton2
              type={ICON_BUTTON_TYPES.SUBMIT}
              oblong={true}
              small
              onClick={handleButtonClick}
              disabled={!submitEnabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default VisualAttention;
